import {
  OpportunityDetail,
  DropdownDetails,
  Comment,
  ConceptResponse,
  updateOpportunity,
  ConceptPayload,
  ConceptSuggestions,
  SiteTransferResponse,
  SiteTransferNote,
  NADWaiverResponse,
  NADWaiverNote,
  NADWaiverNoteRequest,
  CommentRequestPayload,
  SiteTransferInfoPayload,
  SiteTransferNotePayload,
  portfolioNdcStrengthPacksizeResponseInterface,
} from 'Interfaces/BDPortalInterfaces';
import { MoleculeNames } from 'Interfaces/BDPortalInterfaces/MoleculeName';
import { makeAutoObservable, runInAction } from 'mobx';
import { Nullable } from 'primereact/ts-helpers';
import { toast } from 'react-toastify';
import { bdportalScreenServices } from 'services/bdportalScreenServices';

/** Newest comments first (by LastModifiedDate). */
function sortCommentsByLastModifiedDesc(comments: Comment[]): Comment[] {
  return [...comments].sort((a, b) => {
    const tA = a.LastModifiedDate ? new Date(a.LastModifiedDate).getTime() : 0;
    const tB = b.LastModifiedDate ? new Date(b.LastModifiedDate).getTime() : 0;
    return tB - tA;
  });
}

export class BDPortalStore {
  allDrugMoleculesDetailsForSearch: MoleculeNames[] = [];
  isAllDrugsLoading = false;
  filteredDrugMoleculesForSearch: string[] = [];
  drugSearchInput: string = '';
  isOpportunityBoardDetailsLoading = false;
  dropdownDetails: DropdownDetails | null = null;
  saveErrorStatus: number | null = null;
  error: string | number | null = null;
  allOpportunityDetails: OpportunityDetail[] = [];
  board: string = 'NC';
  comments: Comment[] = [];
  concepts: ConceptResponse | null = null;
  currentConceptsIndex: number = 0;
  currentCommentsIndex: number = 0;
  quickLoad: boolean = false;
  quickLoadCards: Set<number> = new Set();
  assigneeUpdatingCards: Set<number> = new Set();
  conceptSuggestions: ConceptSuggestions[] = [];
  iterationNdcPacksizeStrengths: portfolioNdcStrengthPacksizeResponseInterface = [];
  isIterationNdcPacksizeStrengthsLoading: boolean = false;
  conceptSuggLoading: boolean = false;
  opportunityID: number | null = null;
  opportunityIID: number | null = null;

  //filter observables
  searchKeyword: string = '';
  filterAnalyst: number[] | null = null;
  filterAccManagers: number[] | null = null;
  filterStage: number | null = null;
  filterStatus: number | null = null;
  filterSource: number[] | null = null;
  filterAgency: number[] | null = null;
  filterPrimarySupplier: string | null = null;
  filterStartAndEnddates: Nullable<(Date | null)[]> = null;
  filterAnnualRevenueRange: [number, number] = [0, 0];

  // filterPrimarySupplier: string| null = null;
  filterIsPortfolioOpportunity: boolean | null = null;

  siteTransferDetails: SiteTransferResponse | null = null;
  siteTransferLoading: boolean = false;
  siteTransferNotes: SiteTransferNote[] = [];
  siteTransferNoteLoading: boolean = false;

  NADWaierDetails: NADWaiverResponse | null = null;
  NADWaiverLoading: boolean = false;
  NADWaiverNoteLoading: boolean = false;
  NADWaiverNotes: NADWaiverNote[] = [];

  // loader observables
  isDropdownDetailsLoading: boolean = false;
  isAllOpportunityLoading: boolean = false;
  isCommentsDataLoading: boolean = false;
  isConceptsDataLoading: boolean = false;
  isCommentSaving: boolean = false;
  isConceptSaving: boolean = false;
  isSiteTransferDetailsSaving: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }
  handleFetchAllConceptDetailsForSearch = async (_signal?: AbortSignal) => {
    this.isAllDrugsLoading = true;
    try {
      const data = await bdportalScreenServices.getAllMoleculeDetails();
      const moleculeSet: Set<string> = new Set();
      this.allDrugMoleculesDetailsForSearch = [];
      data.forEach(molecule => {
        if (!moleculeSet.has(molecule.MoleculeAndForm)) {
          moleculeSet.add(molecule.MoleculeAndForm);
          this.allDrugMoleculesDetailsForSearch.push(molecule);
        }
      });
    } catch (_err) {
      console.log('Error while, getting all the drug details');
    }
    this.isAllDrugsLoading = false;
  };
  updateBoard = async (updateNCBoard: updateOpportunity, boardType: string) => {
    try {
      const data = await bdportalScreenServices.updateBoard(updateNCBoard, boardType);
      return data;
    } catch (err: any) {
      console.log(err.status);
      this.saveErrorStatus = err.status;
    }
  };
  handleFilterDrugSearch = async () => {
    const tempFilteredConcepts: string[] = [];
    for (let i = 0; i < this.allDrugMoleculesDetailsForSearch.length; i++) {
      const moleculeDetails = this.allDrugMoleculesDetailsForSearch[i];
      if (
        moleculeDetails.MoleculeAndForm.toLocaleLowerCase().search(
          this.drugSearchInput.toLocaleLowerCase()
        ) != -1
      ) {
        tempFilteredConcepts.push(moleculeDetails.MoleculeAndForm);
      }
      if (tempFilteredConcepts.length > 10) break;
    }
    this.filteredDrugMoleculesForSearch = tempFilteredConcepts;
  };
  getDropdownDetails = async (signal?: AbortSignal) => {
    this.isDropdownDetailsLoading = true;
    this.error = null;
    try {
      const response = await bdportalScreenServices.getDropdownDetails(signal);
      this.dropdownDetails = response;
      return response;
    } catch (error: any) {
      this.error = `${'Error fetching dropdown details'}`;
      toast.error(`Error fetching dropdown details: ${error}`);
    } finally {
      this.isDropdownDetailsLoading = false;
    }
  };
  getAllOpportunity = async (boardType: string, signal?: AbortSignal) => {
    this.isAllOpportunityLoading = true;
    this.error = null;
    try {
      const response = await bdportalScreenServices.getAllOpportunity(boardType, signal);
      this.allOpportunityDetails = response;
      return response;
    } catch (error: any) {
      this.error = `${error.response.data.error}`;
      toast.error(`${error.response.data.error}`, {
        position: 'bottom-right',
        autoClose: 5000,
      });
    } finally {
      this.isAllOpportunityLoading = false;
    }
  };

  fetchDropdownBoardDetails = async (signal: AbortSignal | null = null) => {
    this.isOpportunityBoardDetailsLoading = true;
    try {
      await this.getDropdownDetails(signal || undefined);
      await this.getAllOpportunity(this.board, signal || undefined);
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('API call was aborted');
      } else {
        console.error(error);
      }
    } finally {
      this.isOpportunityBoardDetailsLoading = false;
    }
  };

  handleDrugSearchInputChange = (input: string) => {
    this.drugSearchInput = input;
    this.handleFilterDrugSearch();
  };
  getComments = async (opportunityId: number, signal?: AbortSignal) => {
    this.isCommentsDataLoading = true;
    try {
      const response = await bdportalScreenServices.getCommentsByOppID(opportunityId, signal);
      this.comments = sortCommentsByLastModifiedDesc(response);
    } catch (error: any) {
      this.error = `${error.response.data.error}`;
      toast.error(`${error.response.data.error}`, {
        position: 'bottom-right',
        autoClose: 5000,
      });
    } finally {
      this.isCommentsDataLoading = false;
    }
  };
  getConcepts = async (opportunityIterationID: number, signal?: AbortSignal) => {
    this.isConceptsDataLoading = true;
    try {
      const response = await bdportalScreenServices.getConceptByID(opportunityIterationID, signal);
      this.concepts = response;
    } catch (error: any) {
      this.error = `${error.response.data.error}`;
      toast.error(`${error.response.data.error}`, {
        position: 'bottom-right',
        autoClose: 5000,
      });
    } finally {
      this.isConceptsDataLoading = false;
    }
  };
  getConceptsSuggestions = async (OpportunityID: number, signal?: AbortSignal) => {
    this.conceptSuggLoading = true;
    try {
      const response = await bdportalScreenServices.getConceptByOppID(OpportunityID, signal);
      this.conceptSuggestions = response;
    } catch (error: any) {
      this.error = `${error.response.data.error}`;
      toast.error(`${error.response.data.error}`, {
        position: 'bottom-right',
        autoClose: 5000,
      });
    } finally {
      this.conceptSuggLoading = false;
    }
  };
  getGFCNDCPacksize = async (iteration_id: number, signal?: AbortSignal) => {
    this.isIterationNdcPacksizeStrengthsLoading = true;
    try {
      const response = await bdportalScreenServices.getPacksizeNdcStrengthForPortfolioOpportunties(
        iteration_id,
        signal
      );
      this.iterationNdcPacksizeStrengths = response;
    } catch (error: any) {
      this.error = `${error.response.data.error}`;
      toast.error(`${error.response.data.error}`, {
        position: 'bottom-right',
        autoClose: 5000,
      });
    } finally {
      this.isIterationNdcPacksizeStrengthsLoading = false;
    }
  };
  saveComments = async (payload: CommentRequestPayload) => {
    this.isCommentSaving = true;
    try {
      const response: Comment[] = await bdportalScreenServices.saveComments(payload);
      this.comments = sortCommentsByLastModifiedDesc([...this.comments, response[0]]);
    } catch (error: any) {
      this.error = `${error.response.data.error}`;
      toast.error(`${error.response.data.error}`, {
        position: 'bottom-right',
        autoClose: 5000,
      });
    } finally {
      this.isCommentSaving = false;
    }
  };
  saveConcept = async (payload: ConceptPayload[]) => {
    this.isConceptSaving = true;
    try {
      const responses = await bdportalScreenServices.saveConcept(payload);
      runInAction(() => {
        responses.forEach(response => {
          // Find the concept with matching strength
          const conceptToUpdate = this.concepts?.Concept_info.find(
            conceptInfo => conceptInfo.Strength === response.Strength
          );

          // Safely push the new pack size
          if (conceptToUpdate && response.PackSize !== null) {
            conceptToUpdate.PackSize = [...(conceptToUpdate.PackSize || []), response.PackSize];
          }
        });
      });
    } catch (error: any) {
      this.error = `${error.response.data.error}`;
      toast.error(`${error.response.data.error}`, {
        position: 'bottom-right',
        autoClose: 5000,
      });
    } finally {
      this.isConceptSaving = false;
    }
  };
  getSiteTransfer = async (OpportunityIID: number, signal?: AbortSignal) => {
    this.siteTransferLoading = true;
    try {
      const response = await bdportalScreenServices.getSiteTransferByID(OpportunityIID, signal);
      this.siteTransferDetails = response;
      this.siteTransferNotes = response.notes;
      return response;
    } catch (error: any) {
      this.error = `${error.response.data.error}`;
      if (error.status !== 404) {
        toast.error(`${error.response.data.error}`, {
          position: 'bottom-right',
          autoClose: 5000,
        });
      }
      this.siteTransferLoading = false;
    } finally {
      this.siteTransferLoading = false;
    }
  };
  saveSiteTransferNote = async (payload: SiteTransferNotePayload) => {
    this.siteTransferNoteLoading = true;
    try {
      const response = await bdportalScreenServices.saveSiteTransferNote(payload);
      this.siteTransferNotes = response.new_data;
    } catch (error: any) {
      this.error = `${error.response.data.error}`;
      toast.error(`${error.response.data.error}`, {
        position: 'bottom-right',
        autoClose: 5000,
      });
    } finally {
      this.siteTransferNoteLoading = false;
    }
  };
  saveSiteTransferDetails = async (payload: SiteTransferInfoPayload) => {
    try {
      this.isSiteTransferDetailsSaving = true;
      await bdportalScreenServices.saveSiteTransferDetails(payload);
      toast.success('Site Transfer Data saved Successfully.');
    } catch (error: any) {
      this.error = `${error.response.data.error}`;
      toast.error(`${error.response.data.error}`, {
        position: 'bottom-right',
        autoClose: 5000,
      });
    } finally {
      this.isSiteTransferDetailsSaving = false;
    }
  };
  getNADwaiver = async (OpportunityIID: number, signal?: AbortSignal) => {
    this.error = null;
    this.NADWaiverLoading = true;
    try {
      const response = await bdportalScreenServices.getNADWaiverByID(OpportunityIID, signal);
      this.NADWaierDetails = response;
      return response;
    } catch (error: any) {
      this.error = `${error.response.data.error}`;
      if (this.concepts?.Concept_info.length !== 0) {
        toast.error(`${error.response.data.error}`, {
          position: 'bottom-right',
          autoClose: 5000,
        });
      }
      this.NADWaiverLoading = false;
    } finally {
      this.NADWaiverLoading = false;
    }
  };
  saveNADWaiverNote = async (payload: NADWaiverNoteRequest) => {
    this.NADWaiverNoteLoading = true;
    try {
      const response = await bdportalScreenServices.saveNADWaiverNote(payload);
      this.NADWaiverNotes = response.new_data;
    } catch (error: any) {
      this.error = `${error.response.data.error}`;
      toast.error(`${error.response.data.error}`, {
        position: 'bottom-right',
        autoClose: 5000,
      });
    } finally {
      this.NADWaiverNoteLoading = false;
    }
  };

  setBoard = (boardName: string) => (this.board = boardName);
  setCurrentConceptsIndex = (index: number) => (this.currentConceptsIndex = index);
  setCurrentCommentsIndex = (index: number) => (this.currentCommentsIndex = index);
  setQuickLoad = (isQuickload: boolean) => (this.quickLoad = isQuickload);

  addInQuickLoad = (opportunityIID: number) => {
    this.quickLoadCards.add(opportunityIID);
  };

  removeFromQuickLoad = (opportunityIID: number) => {
    this.quickLoadCards.delete(opportunityIID);
  };

  addInAssigneeUpdatingCards = (opportunityIID: number) => {
    this.assigneeUpdatingCards.add(opportunityIID);
  };

  removeFromAssigneeUpdatingCards = (opportunityIID: number) => {
    this.assigneeUpdatingCards.delete(opportunityIID);
  };

  setOpportunityID = (oppID: number) => (this.opportunityID = oppID);
  setSearchKeyword = (searchKeyword: string) => (this.searchKeyword = searchKeyword);
  setOpportunityIID = (opportunityIID: number) => (this.opportunityIID = opportunityIID);
  setFilterAnalyst = (selectedAnalyst: number[]) => (this.filterAnalyst = selectedAnalyst);
  setFilterAccManagers = (accManagers: number[]) => (this.filterAccManagers = accManagers);

  setFilterStage = (filterStage: number) => (this.filterStage = filterStage);
  setFilterStatus = (filterStatus: number) => (this.filterStatus = filterStatus);
  setAllOpportunityDetails = (allOpportunityDetails: OpportunityDetail[]) =>
    (this.allOpportunityDetails = allOpportunityDetails);

  setFilterSource = (sourceIds: number[]) => (this.filterSource = sourceIds);
  setFilterAgency = (filterAgencies: number[]) => (this.filterAgency = filterAgencies);
  setFilterPrimarySupplier = (primarySupplier: string) =>
    (this.filterPrimarySupplier = primarySupplier);

  setFilterStartAndEndDate = (dates: Nullable<(Date | null)[]>) => {
    this.filterStartAndEnddates = dates;
  };

  setIsFilterPortfolioOpportunity = (value: boolean | null) => {
    this.filterIsPortfolioOpportunity = value;
  };

  setFilterEstimatedRevenueRange = (revenueRanges: [number, number]) => {
    this.filterAnnualRevenueRange = revenueRanges;
  };

  // filterSource: number[] | null = null;
  // filterAgency: number[] | null = null;
  // filterPrimarySupplier: string | null = null;

  handleClearFilters = () => {
    this.searchKeyword = '';
    this.filterAnalyst = null;
    this.filterAccManagers = null;
    this.filterStage = null;
    this.filterStatus = null;
    this.filterSource = null;
    this.filterAgency = null;
    this.filterPrimarySupplier = null;
    this.filterStartAndEnddates = null;
    this.filterAnnualRevenueRange = [0, 0];
    this.filterIsPortfolioOpportunity = null;
  };

  resetStore = () => {
    // Reset all observable properties
    this.allDrugMoleculesDetailsForSearch = [];
    this.isAllDrugsLoading = false;
    this.filteredDrugMoleculesForSearch = [];
    this.drugSearchInput = '';
    this.isOpportunityBoardDetailsLoading = false;
    this.dropdownDetails = null;
    this.saveErrorStatus = null;
    this.error = null;
    this.allOpportunityDetails = [];
    this.board = 'NC';
    this.comments = [];
    this.concepts = null;
    this.currentConceptsIndex = 0;
    this.currentCommentsIndex = 0;
    this.quickLoad = false;
    this.quickLoadCards = new Set();
    this.assigneeUpdatingCards = new Set();
    this.conceptSuggestions = [];
    this.iterationNdcPacksizeStrengths = [];
    this.isIterationNdcPacksizeStrengthsLoading = false;
    this.conceptSuggLoading = false;
    this.opportunityID = null;
    this.opportunityIID = null;

    // Reset filter observables
    this.handleClearFilters();

    // Reset site transfer and NAD waiver details
    this.siteTransferDetails = null;
    this.siteTransferLoading = false;
    this.siteTransferNotes = [];
    this.siteTransferNoteLoading = false;
    this.NADWaierDetails = null;
    this.NADWaiverLoading = false;
    this.NADWaiverNoteLoading = false;
    this.NADWaiverNotes = [];

    // Reset loader observables
    this.isDropdownDetailsLoading = false;
    this.isAllOpportunityLoading = false;
    this.isCommentsDataLoading = false;
    this.isConceptsDataLoading = false;
    this.isCommentSaving = false;
    this.isConceptSaving = false;
    this.isSiteTransferDetailsSaving = false;
  };
}
